import { AddToCart } from '@theme/cart-api';
import { responsive } from '@theme/responsive';
import { globalPopup } from '@theme/popup';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';
import { IOSScrollLock } from '@theme/ios-scroll-lock';

class StickyAtc extends HTMLElement {
  #host = null;
  #variantInput = null;
  #priceEl = null;
  #compareEl = null;
  #badgeEl = null;
  #button = null;
  #notifyPopup = null;
  #form = null;
  #toggleBtn = null;
  #dropdown = null;
  #dropdownContent = null;
  #addToCart = null;
  #dropdownCloseTimeout = null;
  #dropdownTransitionEnd = null;

  #currentVariant = null;
  #comboData = null;
  #upsellType = null;
  #initialPriceText = '';
  #discountPercent = 10;
  #observer = null;
  #footerObserver = null;
  #defaultText = '';
  #setText = '';
  #initialOos = false;
  #scrollLocked = false;
  #shown = false;
  #triggerPassed = false;
  #atFooter = false;
  #drawerOpen = false;
  #hideTransitionEnd = null;
  #hideFallbackTimer = null;
  #deferModeTimer = null;
  #showRaf1 = null;
  #showRaf2 = null;

  connectedCallback() {
    this.#host = this.closest('main-product');
    this.#variantInput = this.querySelector('[data-sticky-variant-id]');
    this.#priceEl = this.querySelector('[data-sticky-price]');
    this.#compareEl = this.querySelector('[data-sticky-compare]');
    this.#initialPriceText = this.#priceEl?.textContent.trim() || '';
    this.#badgeEl = this.querySelector('[data-sticky-badge]');
    this.#button = this.querySelector('[data-action="add-to-cart"]');
    this.#notifyPopup = document.querySelector('notify-popup');
    this.#form = this.querySelector('[data-sticky-form]');
    this.#toggleBtn = this.querySelector('[data-sticky-toggle]');
    this.#dropdown = this.querySelector('[data-sticky-dropdown]');
    this.#dropdownContent = this.querySelector('[data-sticky-dropdown-content]');

    this.#discountPercent = parseFloat(this.dataset.discountPercent) || 10;
    this.#defaultText = this.dataset.defaultText || 'Add to bag';
    this.#setText = this.dataset.setText || 'Add set to bag';
    this.#initialOos = this.dataset.oos === 'true';

    try {
      this.#addToCart = new AddToCart(this);
    } catch (err) {
      console.error('sticky-atc: AddToCart init failed', err);
    }

    this.#detectUpsell();
    this.#bindEvents();
    this.#observeTriggers();
    this.#updateOosState();

    // Standard popup/drawer pattern: while hidden the element is display:none, not
    // just translated off-screen. Starts hidden; the IntersectionObserver animates
    // it in once the user scrolls past the upsell trigger.
    this.style.display = 'none';
  }

  disconnectedCallback() {
    this.#host?.removeEventListener('pdp:variant-update', this.#onVariantUpdate);
    document.removeEventListener('combo:variant-changed', this.#onComboVariantChanged);
    document.removeEventListener('drawer:opening', this.#onDrawerOpening);
    document.removeEventListener('drawer:closed', this.#onDrawerClosed);
    this.#observer?.disconnect();
    this.#footerObserver?.disconnect();
    this.#toggleBtn?.removeEventListener('click', this.#onToggle);
    this.#button?.removeEventListener('click', this.#onAddToCart);
    this.#button?.removeEventListener('click', this.#onButtonClickCapture, true);
    this.#clearDropdownCloseFallback();
    this.#clearHideTransition();
    if (this.#deferModeTimer) clearTimeout(this.#deferModeTimer);
    this.#cancelShowRaf();
    this.#unlockScroll();
  }

  #bindEvents() {
    this.#host?.addEventListener('pdp:variant-update', this.#onVariantUpdate);
    // global-scope: combo:variant-changed bubbles to document from the sibling combo.
    document.addEventListener('combo:variant-changed', this.#onComboVariantChanged);
    // Hide the sticky bar while any drawer/overlay (cart, menu, search) is open; the
    // canonical DrawerComponent dispatches drawer:opening on document and drawer:closed
    // bubbles up to it. Restores on close via the normal scroll/footer visibility rules.
    document.addEventListener('drawer:opening', this.#onDrawerOpening);
    document.addEventListener('drawer:closed', this.#onDrawerClosed);
    this.#toggleBtn?.addEventListener('click', this.#onToggle);
    this.#button?.addEventListener('click', this.#onAddToCart);
    this.#button?.addEventListener('click', this.#onButtonClickCapture, true);
  }

  #onAddToCart = () => {
    if (this.#toggleBtn?.getAttribute('aria-expanded') === 'true') {
      this.#closeDropdown();
      // Reset to default mode AFTER this click's cart-api submit. cart-api's delegated
      // handler runs in the bubble phase (after this target-phase listener) and
      // serializes the form synchronously; switching to default here first would reset
      // the variant input from the bundle variant back to the main product -> the combo
      // would add the single product instead of the bundle. Defer past the submit.
      if (this.#upsellType === 'combo') {
        this.#deferModeTimer = setTimeout(() => {
          this.#deferModeTimer = null;
          this.#setMode('default');
        }, 0);
      }
    }
  };

  #detectUpsell() {
    // Synchronous detection is safe regardless of Customizer block order: every
    // block (and its light-DOM children) is server-rendered by {% content_for
    // 'blocks' %} into the initial HTML, and this file is a deferred `type=module`
    // loader, so the whole document is parsed before connectedCallback runs.
    // <combo-upsell> is therefore always present here if it exists on the page;
    // we only read its server-rendered markup (cards/form/price), never its JS state.
    // global-scope: upsell/footer elements are page-level siblings outside this component
    const combo = document.querySelector('combo-upsell');
    if (combo) {
      const cards = Array.from(combo.querySelectorAll('.combo-upsell__card'));
      const bundleForm = combo.querySelector('form');
      const bundleVariantId = bundleForm?.querySelector('[name="id"]')?.value;

      if (bundleVariantId) {
        this.#upsellType = 'combo';
        // Cache the combo's current variant id + prices. Re-read on every
        // combo:variant-changed (colour switch) so set-mode adds the right variant.
        this.#refreshComboData();
        if (this.#dropdownContent) {
          // Clone the live card nodes instead of outerHTML->innerHTML: no string
          // serialization, so no re-parse/injection surface from merchant content.
          const cardsWrap = document.createElement('div');
          cardsWrap.className = 'combo-upsell__cards';
          cards.forEach((c) => cardsWrap.appendChild(c.cloneNode(true)));
          this.#dropdownContent.replaceChildren(cardsWrap);
        }
        this.dataset.upsellType = 'combo';
        return;
      }
    }

    const regular = document.querySelector('[data-regular-upsell]');
    if (regular && regular.querySelector('product-card')) {
      // Regular upsell: NO dropdown. The sticky bar just shows the product name
      // (the title, revealed on mobile via CSS) + the ATC button. The expandable
      // toggle/dropdown is combo-only.
      this.#upsellType = 'regular';
      this.dataset.upsellType = 'regular';
      if (this.#toggleBtn) this.#toggleBtn.hidden = true;
      return;
    }

    // No upsell — hide toggle entirely
    if (this.#toggleBtn) this.#toggleBtn.hidden = true;
    this.#upsellType = null;
  }

  #observeTriggers() {
    const trigger = document.querySelector('combo-upsell, [data-regular-upsell], .product-usps');
    if (!trigger) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            this.#triggerPassed = true;
            this.#applyVisibility();
          } else if (entry.isIntersecting) {
            this.#triggerPassed = false;
            this.#applyVisibility();
            this.#closeDropdown();
          }
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.#observer.observe(trigger);

    const footer = document.querySelector('[data-footer-trigger]');
    if (footer) {
      this.#footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.#atFooter = true;
              this.#applyVisibility();
              this.#closeDropdown();
            } else {
              this.#atFooter = false;
              this.#applyVisibility();
            }
          });
        },
        { threshold: 0 }
      );
      this.#footerObserver.observe(footer);
    }
  }

  // Visible only after scrolling past the upsell trigger, not at the footer, and not
  // while a drawer/overlay (cart, menu, search) is open.
  #applyVisibility() {
    const shown = this.#triggerPassed && !this.#atFooter && !this.#drawerOpen;
    if (shown === this.#shown) return;
    this.#shown = shown;
    shown ? this.#show() : this.#hide();
  }

  #onDrawerOpening = () => {
    this.#drawerOpen = true;
    this.#applyVisibility();
  };

  #onDrawerClosed = () => {
    this.#drawerOpen = false;
    this.#applyVisibility();
  };

  #show() {
    this.#clearHideTransition();
    this.#cancelShowRaf();
    // Take the element out of display:none first, then flip the visible attribute
    // on a later frame so the transform transition has a "from" state to animate.
    this.style.display = '';
    this.dataset.atFooter = 'false';
    this.#showRaf1 = requestAnimationFrame(() => {
      this.#showRaf2 = requestAnimationFrame(() => {
        this.dataset.visible = 'true';
      });
    });
  }

  #hide() {
    this.#cancelShowRaf();
    // Drive the slide-out transition, then drop to display:none on transitionend.
    this.dataset.visible = 'false';
    this.dataset.atFooter = this.#atFooter ? 'true' : 'false';
    this.#waitForHideTransition();
  }

  // Mirror of the drawer close pattern: wait for the transform transitionend
  // (filtered to this element + the transform property), with a timeout fallback
  // for interrupted / reduced-motion transitions that never fire the event.
  #waitForHideTransition() {
    this.#clearHideTransition();

    const finalize = () => {
      this.#clearHideTransition();
      // Guard against a re-show that happened mid slide-out.
      if (!this.#shown) this.style.display = 'none';
    };

    this.#hideTransitionEnd = (event) => {
      if (event.target !== this) return;
      if (event.propertyName !== 'transform') return;
      finalize();
    };

    this.addEventListener('transitionend', this.#hideTransitionEnd);
    this.#hideFallbackTimer = window.setTimeout(finalize, this.#getTransitionMs() + 100);
  }

  #clearHideTransition() {
    if (this.#hideTransitionEnd) {
      this.removeEventListener('transitionend', this.#hideTransitionEnd);
      this.#hideTransitionEnd = null;
    }
    if (this.#hideFallbackTimer) {
      window.clearTimeout(this.#hideFallbackTimer);
      this.#hideFallbackTimer = null;
    }
  }

  #cancelShowRaf() {
    if (this.#showRaf1) {
      cancelAnimationFrame(this.#showRaf1);
      this.#showRaf1 = null;
    }
    if (this.#showRaf2) {
      cancelAnimationFrame(this.#showRaf2);
      this.#showRaf2 = null;
    }
  }

  #getTransitionMs() {
    const raw = getComputedStyle(this).getPropertyValue('--sticky-atc-duration').trim();
    if (raw.endsWith('ms')) return parseFloat(raw) || 300;
    return (parseFloat(raw) || 0.3) * 1000;
  }

  #onToggle = () => {
    const expanded = this.#toggleBtn.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      this.#closeDropdown();
      if (this.#upsellType === 'combo') this.#setMode('default');
    } else {
      this.#openDropdown();
      if (this.#upsellType === 'combo' && this.#comboData) this.#setMode('set');
    }
  };

  #openDropdown() {
    if (!this.#dropdown) return;
    this.#clearDropdownCloseFallback();

    this.#dropdown.removeAttribute('hidden');
    this.#toggleBtn?.setAttribute('aria-expanded', 'true');
    // Wait one frame so the element is rendered before measuring scrollHeight
    // and starting the max-height transition.
    requestAnimationFrame(() => {
      if (!this.#dropdown) return;
      const dropdownHeight = this.#getDropdownHeight();
      this.#dropdown.setAttribute('aria-hidden', 'false');
      this.dataset.dropdownOpen = 'true';
      this.#setDesktopFormOffset(this.#getDesktopFormOffset(dropdownHeight), this.#getDesktopFormSpace());
      this.#dropdown.style.maxHeight = `${dropdownHeight}px`;
    });
    this.#lockScroll();
  }

  #closeDropdown() {
    if (!this.#dropdown) return;
    const isExpanded = this.#toggleBtn?.getAttribute('aria-expanded') === 'true';
    if (!isExpanded && this.#dropdown.hasAttribute('hidden')) return;

    this.#clearDropdownCloseFallback();

    this.#dropdown.setAttribute('aria-hidden', 'true');
    this.#dropdown.style.maxHeight = `${this.#dropdown.getBoundingClientRect().height}px`;
    this.#toggleBtn?.setAttribute('aria-expanded', 'false');
    this.#unlockScroll();

    const finishClose = () => {
      this.#clearDropdownCloseFallback();
      // Guard against re-open mid-transition.
      if (this.#toggleBtn?.getAttribute('aria-expanded') === 'true') return;
      this.dataset.dropdownOpen = 'false';
      requestAnimationFrame(() => {
        this.#dropdown?.setAttribute('hidden', '');
      });
    };

    this.#dropdownTransitionEnd = (e) => {
      if (e.propertyName !== 'max-height') return;
      finishClose();
    };
    this.#dropdown.addEventListener('transitionend', this.#dropdownTransitionEnd);
    // timeout: fallback if max-height transitionend never fires (interrupted
    // transition, or reduced-motion 0.01ms duration). Cleared in #clearDropdownCloseFallback.
    this.#dropdownCloseTimeout = window.setTimeout(finishClose, 350);

    requestAnimationFrame(() => {
      if (!this.#dropdown) return;
      this.#setDesktopFormOffset(0, 0);
      this.#dropdown.style.maxHeight = '0px';
    });
  }

  #setDesktopFormOffset(offset, space) {
    if (!responsive.matchesBreakpoint('lap_and_up')) {
      this.style.removeProperty('--sticky-atc-form-offset');
      this.style.removeProperty('--sticky-atc-form-space');
      return;
    }

    this.style.setProperty('--sticky-atc-form-offset', `${offset}px`);
    this.style.setProperty('--sticky-atc-form-space', `${space}px`);
  }

  #getDesktopFormOffset(dropdownHeight = this.#getDropdownHeight()) {
    if (!this.#dropdown || !this.#form) return 0;

    const dropdownRect = this.#dropdown.getBoundingClientRect();
    const formRect = this.#form.getBoundingClientRect();
    const dropdownMarginTop = this.#getPixelValue(getComputedStyle(this.#dropdown).marginTop);
    const dropdownTop = dropdownRect.top - dropdownMarginTop;

    return Math.max(0, dropdownTop - formRect.top + dropdownHeight + this.#getDesktopFormGap());
  }

  #getDropdownHeight() {
    if (!this.#dropdown) return 0;

    return this.#dropdownContent?.getBoundingClientRect().height || this.#dropdown.scrollHeight;
  }

  #getDesktopFormSpace() {
    if (!this.#form) return 0;

    return this.#form.getBoundingClientRect().height + this.#getDesktopFormGap();
  }

  #getDesktopFormGap() {
    const gap = getComputedStyle(this).getPropertyValue('--sticky-atc-form-gap').trim();
    return this.#getPixelValue(gap) || 20;
  }

  #getPixelValue(value) {
    const numericValue = parseFloat(value);
    if (!numericValue) return 0;
    if (value.endsWith('rem')) {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return numericValue * rootFontSize;
    }

    return numericValue;
  }

  #clearDropdownCloseFallback() {
    if (this.#dropdownTransitionEnd && this.#dropdown) {
      this.#dropdown.removeEventListener('transitionend', this.#dropdownTransitionEnd);
    }

    if (this.#dropdownCloseTimeout) {
      window.clearTimeout(this.#dropdownCloseTimeout);
    }

    this.#dropdownTransitionEnd = null;
    this.#dropdownCloseTimeout = null;
  }

  // Canonical drawer scroll-lock trio (mirrors drawer-component): body-scroll-lock
  // on the scrollable region + .scrollLock class + ref-counted IOSScrollLock. The
  // #scrollLocked guard keeps lock/unlock balanced (closeDropdown fires from several
  // observers) so the iOS ref-count never desyncs.
  #lockScroll() {
    if (this.#scrollLocked) return;
    disableBodyScroll(this.#dropdown, bodyScrollLockOptions);
    document.body.classList.add('scrollLock');
    IOSScrollLock.lock();
    this.#scrollLocked = true;
  }

  #unlockScroll() {
    if (!this.#scrollLocked) return;
    enableBodyScroll(this.#dropdown);
    document.body.classList.remove('scrollLock');
    IOSScrollLock.unlock();
    this.#scrollLocked = false;
  }

  #setMode(mode) {
    this.dataset.mode = mode;
    this.#updateOosState();
    if (mode === 'set' && this.#comboData) {
      this.#applySetMode();
    } else {
      this.#applyDefaultMode();
    }
  }

  #applyDefaultMode() {
    if (this.#variantInput && this.#currentVariant) {
      this.#variantInput.value = String(this.#currentVariant.id);
    }
    // Instant price swap back to variant price (before compare slides out)
    if (this.#priceEl) {
      const variantPrice = this.#currentVariant?.price_formatted || this.#initialPriceText;
      this.#priceEl.textContent = variantPrice;
    }
  }

  #applySetMode() {
    if (!this.#comboData) return;
    if (this.#variantInput) this.#variantInput.value = this.#comboData.variantId;

    if (this.#compareEl) {
      this.#compareEl.textContent = this.#comboData.comparePrice || '';
    }

    if (this.#priceEl) this.#priceEl.textContent = this.#comboData.setPrice;
  }

  // Re-read the combo's live variant id + prices from its markup. The combo block
  // updates [data-combo-price]/[data-combo-compare] and the form id input on colour
  // change, so this keeps the cached set-mode data in sync with the chosen colour.
  #refreshComboData() {
    if (this.#upsellType !== 'combo') return;
    // global-scope: combo-upsell is a page-level sibling outside this component.
    const combo = document.querySelector('combo-upsell');
    if (!combo) return;
    const variantId = combo.querySelector('form [name="id"]')?.value || '';
    const setPriceEl = combo.querySelector('.combo-upsell__set-price');
    const compareEl = combo.querySelector('.combo-upsell__compare-price');
    this.#comboData = {
      variantId,
      setPrice: setPriceEl?.textContent.trim() || '',
      comparePrice:
        compareEl && !compareEl.hasAttribute('hidden')
          ? compareEl.textContent.trim()
          : '',
    };
  }

  #onComboVariantChanged = () => {
    this.#refreshComboData();
    // If the dropdown is open in set mode, reflect the new variant/prices live.
    if (this.dataset.mode === 'set') this.#applySetMode();
  };

  #onVariantUpdate = (event) => {
    const variant = event.detail?.variant;
    if (!variant) return;

    this.#currentVariant = variant;

    if (this.dataset.mode === 'default' && this.#priceEl && variant.price_formatted) {
      this.#priceEl.textContent = variant.price_formatted;
    }

    if (this.dataset.mode === 'default' && this.#variantInput) {
      this.#variantInput.value = String(variant.id);
    }

    this.#updateOosState();
  };

  #updateOosState() {
    // Before the first variant update #currentVariant is null -> use the server OOS.
    const oos = this.#currentVariant ? this.#currentVariant.available === false : this.#initialOos;
    // OOS main product = notify in BOTH modes: the combo bundle includes this item,
    // so it cannot be fulfilled while it is sold out. Fall back to disabled if no popup.
    const notify = oos && !!this.#notifyPopup;
    this.dataset.oos = notify ? 'true' : 'false';
    this.#button?.toggleAttribute('disabled', oos && !notify);

    // Sold out collapses to the regular-upsell look; close the combo dropdown if it
    // was open before the variant went out of stock (the toggle is hidden via CSS).
    if (oos && this.#toggleBtn?.getAttribute('aria-expanded') === 'true') {
      this.#closeDropdown();
    }
  }

  #onButtonClickCapture = (event) => {
    if (this.dataset.oos !== 'true') return;
    // Capture phase + stopImmediatePropagation preempts the cart-api submit so the
    // OOS button opens the notify popup instead of adding an unavailable variant.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#notifyPopup) globalPopup.openPopup(this.#notifyPopup);
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
